import * as React from "react";
import { Globe, Languages, Moon, RotateCcw, Save, Sun } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/dashboard/shared";
import { useLanguage } from "@/components/dashboard/language-provider";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { languages, type Language } from "@/lib/i18n";

function SectionCard({ icon: Icon, title, description, children }: { icon: React.ElementType; title: string; description: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3 border-b border-border/60 p-5">
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary"><Icon className="size-4" /></span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        </div>
      </CardHeader>
      <CardContent className="grid gap-5 p-5">{children}</CardContent>
    </Card>
  );
}

function ToggleRow({ title, description, checked, onCheckedChange }: { title: string; description: string; checked: boolean; onCheckedChange: (value: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={title} />
    </div>
  );
}

const defaults = { timezone: "Africa/Dar_es_Salaam", currency: "TZS", dateFormat: "DD MMM YYYY", compact: false, digests: true, alerts: true, updates: false };

export function SettingsPage() {
  const { language, setLanguage } = useLanguage();
  const [theme, setTheme] = usePersistedState<"light" | "dark">("deuxda-theme", "light");
  const [prefs, setPrefs] = usePersistedState("deuxda-settings", defaults);

  React.useEffect(() => { document.documentElement.classList.toggle("dark", theme === "dark"); }, [theme]);

  function update<K extends keyof typeof defaults>(key: K, value: (typeof defaults)[K]) {
    setPrefs((current) => ({ ...current, [key]: value }));
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description="Adjust the language, appearance and regional preferences of the console." />

      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard icon={Languages} title="Language" description="Choose the language used across the whole admin console.">
          <div className="grid gap-2">
            <Label htmlFor="settings-language">Interface language</Label>
            <Select
              value={language}
              onValueChange={(value) => { setLanguage(value as Language); toast.success("Language updated"); }}
            >
              <SelectTrigger id="settings-language" className="w-full"><SelectValue><span data-no-translate>{languages.find((item)=>item.value===language)?.native}</span></SelectValue></SelectTrigger>
              <SelectContent>
                {languages.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    <span className="mr-2">{item.flag}</span><span data-no-translate>{item.native}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2">
            {languages.map((item) => (
              <Button
                key={item.value}
                size="sm"
                variant={language === item.value ? "default" : "outline"}
                onClick={() => { setLanguage(item.value); toast.success("Language updated"); }}
              >
                <span>{item.flag}</span><span data-no-translate>{item.native}</span>
              </Button>
            ))}
          </div>
        </SectionCard>

        <SectionCard icon={theme === "dark" ? Moon : Sun} title="Appearance" description="Use the carefully balanced dark interface.">
          <div className="grid gap-2">
            <Label htmlFor="settings-theme">Theme</Label>
            <Select value={theme} onValueChange={(value) => setTheme(value as "light" | "dark")}>
              <SelectTrigger id="settings-theme" className="w-full"><SelectValue>{theme === "dark" ? "Dark" : "Light"}</SelectValue></SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ToggleRow title="Compact tables" description="Reduce row height for dense data screens." checked={prefs.compact} onCheckedChange={(value) => update("compact", value)} />
        </SectionCard>

        <SectionCard icon={Globe} title="Regional preferences" description="Region and formats used for dates, numbers and currency.">
          <div className="grid gap-2">
            <Label htmlFor="settings-timezone">Timezone</Label>
            <Input id="settings-timezone" value={prefs.timezone} onChange={(event) => update("timezone", event.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="settings-currency">Default currency</Label>
              <Select value={prefs.currency} onValueChange={(value) => update("currency", value)}>
                <SelectTrigger id="settings-currency" className="w-full"><SelectValue>{prefs.currency}</SelectValue></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TZS">TZS</SelectItem>
                  <SelectItem value="CDF">CDF</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="settings-date">Date format</Label>
              <Input id="settings-date" value={prefs.dateFormat} onChange={(event) => update("dateFormat", event.target.value)} />
            </div>
          </div>
        </SectionCard>

        <SectionCard icon={Save} title="Notifications and alerts" description="Choose what the console notifies you about.">
          <ToggleRow title="Email digests" description="A daily summary of marketplace activity." checked={prefs.digests} onCheckedChange={(value) => update("digests", value)} />
          <ToggleRow title="Critical alerts" description="Payment failures, outages and moderation escalations." checked={prefs.alerts} onCheckedChange={(value) => update("alerts", value)} />
          <ToggleRow title="Product updates" description="Occasional messages about new Deuxda features." checked={prefs.updates} onCheckedChange={(value) => update("updates", value)} />
        </SectionCard>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={() => { setPrefs(defaults); toast.success("Preferences reset"); }}><RotateCcw />Reset to defaults</Button>
        <Button onClick={() => toast.success("Settings saved")}><Save />Save preferences</Button>
      </div>
    </div>
  );
}
